import React, { useEffect, useMemo, useRef, useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { useQuery } from '@tanstack/react-query';
import { connect } from 'react-redux';
import { type RouteComponentProps, Switch, withRouter } from 'react-router';
import { compose, setDisplayName } from 'recompose';

import { accountActions } from '../actions/account.actions';
import { appsActions } from '../actions/apps.actions';
import Loading from '../components/Loading';
import {
  getOnboardingConfigs,
  getOnboardingConfigsQueryKey,
  getOnboardingItems,
  getOnboardingItemsQueryKey,
} from '../components/OnboardingV2/onboarding.service';
import { getOrgById } from '../components/RMS/organisation.services';
import { isAcceptInviteFlowInProgress } from '../components/Teammates/teammateutils';
import { useIsWebView } from '../custom-hooks/useIsWebView';
import { usePageTracking } from '../custom-hooks/usePageTracking';
import { gdpr } from '../helpers/gdpr';
import { store } from '../helpers/store';
import { notifyError } from '../layouts/Notify/actions';
import { getAccount } from '../selectors/account.selector';
import { flagService } from '../services';
import { useTracking } from '../services/amplitude/useTracking';
import { sendDatadogRumData } from '../services/datadogRUM';
import {
  initializeDDFeatureFlags,
  setDDFeatureFlagContext,
} from '../services/ddFeatureFlags/ddFeatureFlags';
import { updateIntercomCompany } from '../services/intercom.service';
import { useIntercom, useProductionIntercomAppId } from '../services/intercom/useIntercom';
import { initialise as initaliseDataDogLogging } from '../services/loggerService';
import { accountRoutesConst } from './account.routes';
import { isNoOrgRedirectToAppHomeEnabled } from './noOrganisationAccessFlags';
import {
  acceptAllInvitesAndRouteToFirstAcceptedBrand,
  createRoute,
  DEFAULT_MIN_LOADING_TIMEOUT,
  getAppId,
  getIsLoggingIn,
  isAppLessPrivateRoute,
  isFirstSetupV2Route,
  isOrgInPathName,
  isPublicRoute,
} from './utils';

// App-less routes do not carry an app id in the URL, so we use the existing
// flipdish-global lookup token to resolve an accessible app when needed.
const DEFAULT_APP_LOOKUP_ID = 'flipdish-global';
const getResolvedAppHomePath = (appId: string) => `/${appId}/home`;

type Props = RouteComponentProps & MappedState & MappedDispatch;
const AuthRouter: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const isAcceptInviteInProgress = isAcceptInviteFlowInProgress();

  const {
    account,
    currentApp,
    registeredRoutes,
    location,
    history,
    permissions,
    setCurrentApp,
    dispatchNotifyError,
    intercomCompanySyncEnabled,
  } = props;

  const isWebView = useIsWebView();
  const { identifyUser } = useTracking(account);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [intercomInitialized, setIntercomInitialized] = useState(false);
  const hasViewOnboardingPermissions = permissions.includes('ViewOnboarding');
  const { update: updateIntercom } = useIntercom();
  const isAppLessRoute = isAppLessPrivateRoute(location.pathname);
  const isNoOrganisationAccessRoute = location.pathname === accountRoutesConst.NoOrganisationAccess;

  //#region onboarding v2
  const { data: onboardingConfigs, isFetched: onboardConfigsFetched } = useQuery({
    queryKey: [getOnboardingConfigsQueryKey, currentApp.AppId],
    queryFn: async () => {
      return await getOnboardingConfigs(currentApp.AppId ?? '');
    },
    enabled: !!currentApp.AppId && hasViewOnboardingPermissions,
  });

  const onboardingConfig = useMemo(() => {
    return onboardingConfigs?.Data.find(
      (cfg) => cfg.ConfigType === 'OnboardingWizard' && cfg.IsEnabled
    );
  }, [onboardingConfigs]);

  useEffect(() => {
    if (
      (onboardConfigsFetched && !onboardingConfig?.IsEnabled) ||
      (!hasViewOnboardingPermissions && permissions?.length)
    ) {
      setInitialized(true);
    }
  }, [onboardConfigsFetched, onboardingConfig, permissions]);

  const { data: onboardingV2Steps, isFetched: isOnboardingV2StepsFetched } = useQuery({
    queryKey: [getOnboardingItemsQueryKey, currentApp.AppId],
    queryFn: async () => {
      const response = await getOnboardingItems(
        currentApp.AppId ?? '',
        onboardingConfig?.WebActivationWizardMilestoneId
      );
      return response?.Data?.RootItems?.[0]?.Items?.[0]?.Items?.filter(
        (step) => step?.Status !== 'Completed'
      );
    },
    enabled: !!onboardingConfig?.IsEnabled,
  });

  useEffect(() => {
    if (isOnboardingV2StepsFetched) {
      setInitialized(true);
    }
  }, [isOnboardingV2StepsFetched]);

  const isFirstSetupV2 = !!onboardingV2Steps?.length;

  //#endregion

  //#region initialize
  const startLoadingTime = useRef<number>();
  const pathBefore = useRef<string>();
  useEffect(() => {
    startLoadingTime.current = Date.now();

    (function setPathBefore() {
      const { pathname } = location;
      // now any route matches private /:appId/ :(
      if (
        pathname !== '/' &&
        !isAppLessPrivateRoute(pathname) &&
        !isOrgInPathName(pathname) &&
        !isPublicRoute(pathname) &&
        !isFirstSetupV2Route(pathname)
      ) {
        pathBefore.current = pathname;
      }
    })();

    props.getAccountDetails().catch(() => {
      // not login
      setTimeout(
        () => {
          setInitialized(true);
        },
        DEFAULT_MIN_LOADING_TIMEOUT - (Date.now() - startLoadingTime.current!)
      );
    });
  }, []);

  useEffect(() => {
    // Prevents briefly showing portal home screen before onboarding screens display
    if (getIsLoggingIn() && account.authorized && !onboardConfigsFetched) {
      setInitialized(false);
      setLoading(true);
    }
  }, [account, onboardConfigsFetched]);

  useEffect(() => {
    if (account.authorized && account.AccountId) {
      (async () => {
        if (isNoOrganisationAccessRoute && currentApp.AppId) {
          if (isNoOrgRedirectToAppHomeEnabled()) {
            history.replace(getResolvedAppHomePath(currentApp.AppId));
          }
          return;
        }

        if (!currentApp.AppId) {
          if (isOrgInPathName(location.pathname)) {
            setInitialized(true);
            setLoading(false);
            return;
          }

          // This will return FD-global app for FD staff always
          // non fd but existing client will get one or more apps back based on their account cookie
          // new signup will get no apps back

          const app = await setCurrentApp(
            isNoOrganisationAccessRoute ? DEFAULT_APP_LOOKUP_ID : getAppId(pathBefore),
            { suppressNoAppFoundError: account.IsSelfServeUser || isNoOrganisationAccessRoute }
          );

          if (isNoOrganisationAccessRoute && app?.AppId) {
            if (isNoOrgRedirectToAppHomeEnabled()) {
              history.replace(getResolvedAppHomePath(app.AppId));
            }
            setInitialized(true);
            setLoading(false);
            return;
          }

          if (!app?.AppId) {
            if (account.IsSelfServeUser && getIsLoggingIn()) {
              history.replace(accountRoutesConst.NoOrganisationAccess);
              pathBefore.current = undefined;
              setInitialized(true);
              setLoading(false);
              return;
            }

            if (isAppLessPrivateRoute(location.pathname)) {
              pathBefore.current = undefined;
              setInitialized(true);
              setLoading(false);
              return;
            }

            if (account.IsSelfServeUser) {
              history.replace(accountRoutesConst.NoOrganisationAccess);
              pathBefore.current = undefined;
            } else {
              if (!isAcceptInviteInProgress && getIsLoggingIn()) {
                await acceptAllInvitesAndRouteToFirstAcceptedBrand({
                  email: account?.Email ?? '',
                  dispatchNotifyError,
                });
              } else {
                dispatchNotifyError();
              }
            }
            setInitialized(true);
            setLoading(false);
            return;
          }

          if (onboardingV2Steps?.length === 0 && pathBefore.current) {
            const path = pathBefore.current;
            pathBefore.current = undefined;
            if (path !== location.pathname) {
              history.replace(path);
            }
          }
        }
      })();
    }
  }, [
    account,
    onboardingV2Steps,
    currentApp,
    history,
    location.pathname,
    setCurrentApp,
    dispatchNotifyError,
    isAcceptInviteInProgress,
    isNoOrganisationAccessRoute,
  ]);
  //#endregion

  //#region page events trackers
  usePageTracking();
  //#endregion

  //#region GDPR -init on first load if not on create_password page
  gdpr.useInitGdpr();
  //#endregion

  //#region Datadog OpenFeature - init on first load independently of GDPR-controlled RUM
  useEffect(() => {
    initializeDDFeatureFlags({
      enableExposureLogging: true,
      enableRumFeatureFlagTracking: true,
    });
  }, []);
  //#endregion

  //#region Split IO -init on first load to retrieve feature flags from Split
  const isFlipdishStaff = permissions?.includes('FlipdishStaff');
  useEffect(() => {
    const isLoggedIn = !!account?.Email;
    const TRAFFIC_TYPE = flagService.TRAFFIC_TYPE;
    const splitParams = {
      appId: currentApp.AppId,
      orgId: currentApp.OrgId,
      isFlipdishStaff,
      userId: account.AccountId || account.DeviceId,
      languageId: account.Language,
      trafficType: isLoggedIn
        ? TRAFFIC_TYPE.USER_TRAFFIC_TYPE
        : TRAFFIC_TYPE.ANONYMOUS_USER_TRAFFIC_TYPE,
    };

    flagService.initialiseSplit(store, splitParams);
    sendDatadogRumData(account, currentApp);
    setDDFeatureFlagContext(account, currentApp, isFlipdishStaff);
    identifyUser();
  }, [account?.AccountId, currentApp?.AppId, isFlipdishStaff]);
  //#endregion

  //#region DataDog -init on first load to allow logging of errors
  useEffect(() => {
    initaliseDataDogLogging();
  }, []);
  //#endregion

  useEffect(() => {
    if (loading && isAcceptInviteInProgress && account.authorized) {
      setLoading(false);
    }
  }, [loading, isAcceptInviteInProgress, account.authorized]);

  //#region Intercom
  const allowIntercomForFlipdishStaff = useProductionIntercomAppId ? !isFlipdishStaff : true;
  // Initialize Intercom after permissions are loaded, but only for non-staff users in production
  useEffect(() => {
    const permissionsLoaded = permissions && permissions.length > 0;
    if (
      permissionsLoaded &&
      allowIntercomForFlipdishStaff &&
      intercomCompanySyncEnabled &&
      !intercomInitialized &&
      !isWebView &&
      !isAppLessRoute
    ) {
      (async () => {
        await gdpr.initGdprIntercomAfterLoad();
        setIntercomInitialized(true);
      })();
    }
  }, [
    permissions,
    allowIntercomForFlipdishStaff,
    intercomCompanySyncEnabled,
    intercomInitialized,
    isWebView,
    isAppLessRoute,
  ]);

  useEffect(() => {
    if (intercomInitialized) {
      updateIntercom({ hide_default_launcher: isWebView || isAppLessRoute });
    }
  }, [isWebView, isAppLessRoute, intercomInitialized]);

  // Update Intercom with current organization when they change
  useEffect(() => {
    const { OrgId: orgId } = currentApp ?? {};
    if (
      orgId &&
      intercomCompanySyncEnabled &&
      allowIntercomForFlipdishStaff &&
      intercomInitialized
    ) {
      (async () => {
        try {
          const orgInformation = await getOrgById(orgId);
          if (orgInformation.data.data) {
            await updateIntercomCompany(orgId, {
              orgName: orgInformation.data.data.name,
            });
          }

          updateIntercom({
            'Portal language': account.Language,
            created_at: account.CreatedAt
              ? Math.floor(new Date(account.CreatedAt).getTime() / 1000)
              : undefined,
          });
        } catch (error) {
          console.error('Error updating Intercom company', error);
        }
      })();
    }
  }, [
    currentApp?.OrgId,
    intercomCompanySyncEnabled,
    allowIntercomForFlipdishStaff,
    intercomInitialized,
  ]);
  //#endregion

  const routes = useMemo(() => {
    if (loading && !isAcceptInviteInProgress) {
      return null;
    }

    if (!account.authorized) {
      datadogRum.setUserProperty('setup_status', 'not_authenticated');
      datadogRum.setGlobalContextProperty('setup_status', 'not_authenticated');
      return registeredRoutes
        .filter((r) => r.group === 'public' || r.group === 'publicAndPrivate')
        .map((r) => createRoute(r, permissions));
    }

    if (isOrgInPathName(location.pathname)) {
      return null;
    }

    if (isFirstSetupV2) {
      datadogRum.setUserProperty('setup_status', 'first_time_setup_V2');
      datadogRum.setGlobalContextProperty('setup_status', 'first_time_setup_V2');
      return registeredRoutes
        .filter((r) => r.group === 'first_time_setup_V2' || r.path === accountRoutesConst.Logout)
        .map((r) => createRoute(r, permissions));
    }

    datadogRum.setUserProperty('setup_status', 'logged_in');
    datadogRum.setGlobalContextProperty('setup_status', 'logged_in');

    return registeredRoutes
      .filter((r) => r.group === 'private' || r.group === 'publicAndPrivate')
      .map((r) => createRoute(r, permissions));
  }, [loading, account, registeredRoutes, permissions, isFirstSetupV2, location.pathname]);

  if (loading) {
    return (
      <Loading
        fullscreen
        animate={!initialized}
        onAnimationEnd={() => {
          // When setInitialized(true) onAnimationEnd sets loading false
          setLoading(false);
        }}
      />
    );
  }

  return <Switch>{routes}</Switch>;
};

type MappedState = ReturnType<typeof mapStateToProps>;
const mapStateToProps = (state: AppState) => {
  return {
    account: getAccount(state),
    currentApp: state.currentApp,
    permissions: state.permissions,
    registeredRoutes: state.routing.routes,
    intercomCompanySyncEnabled: flagService.isFlagOn(state, 'enableIntercomCompanySync'),
  };
};

type MappedDispatch = ReturnType<typeof mapDispatchToProps>;
const mapDispatchToProps = (dispatch: ThunkDispatch) => ({
  getAccountDetails: () => dispatch(accountActions.getAccountDetails(true)),
  setCurrentApp: (
    appId: string | undefined,
    options?: Parameters<typeof appsActions.setCurrentApp>[1]
  ) => dispatch(appsActions.setCurrentApp(appId, options)),
  dispatchNotifyError: () =>
    dispatch(notifyError({ message: 'Something_went_wrong', translate: true })),
});

export default compose<Props, Record<string, never>>(
  setDisplayName('AuthRouter'),
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(AuthRouter);
